/**
 * Thin server-sync layer for the inventory slice of AppContext (lots,
 * cards, storage bins). When a user is signed in, every mutation that
 * touches one of these tables fires a matching API call.
 *
 * Errors are logged to the console and surfaced via the `onError`
 * callback so AppContext can show a sync banner; the in-memory
 * optimistic state is left untouched so the user keeps working — the
 * worst case is a refresh re-fetch.
 */
import { apiFetch } from "@/lib/apiClient";
import type {
  Card,
  CardPatch,
  Container,
  PurchaseLot,
  SortingBin,
  StorageBin,
} from "./AppContext";

// ─── Sorting bin ⇄ API mapping ──────────────────────────────────────────
//
// Client `SortingBin` keys bins by `category` and tracks `count` (sort
// progress). Server `ApiSortingBin` keys by `id` + free-text `name` and
// stores `count` as of the TNK-45 schema fix. Bridge both shapes here so
// the rest of the app keeps using the category-flavored shape.
//
// `id` is derived deterministically from `${lotId}|${category}` — categories
// are an enum and one bin exists per category per lot, so this is unique and
// idempotent (re-sending the same bin updates the same row).

interface ApiSortingBin {
  id: string;
  name: string;
  count: number;
  estimatedCount?: number;
  location: string;
  notes: string;
  costBasis: number;
  createdAt?: string;
}

function binIdFor(lotId: string, category: string): string {
  // Slugify the category — categories like "Common/Uncommon Bulk" and
  // "Trainers/Items/Supporters" contain forward slashes, which survive
  // `encodeURIComponent` but get decoded back to `/` somewhere in the
  // proxy chain (cloudflared/nginx). That splits Express's `:id` path
  // param and every PATCH/DELETE on those bins 404s.
  const slug = category
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
  return `${lotId}|${slug}`;
}

function toApiBin(b: SortingBin, lotId: string): ApiSortingBin {
  return {
    id: binIdFor(lotId, b.category),
    name: b.category,
    count: b.count,
    estimatedCount: b.estimatedCount,
    location: b.location,
    notes: b.notes,
    costBasis: b.costBasis,
  };
}

function fromApiBin(a: ApiSortingBin): SortingBin {
  return {
    category: a.name,
    count: a.count ?? 0,
    estimatedCount: a.estimatedCount,
    location: a.location,
    notes: a.notes,
    costBasis: a.costBasis,
  };
}

interface ApiLotResponse {
  sortingBins?: ApiSortingBin[];
  [k: string]: unknown;
}

function fromApiLot(raw: ApiLotResponse): PurchaseLot {
  const { sortingBins, ...rest } = raw;
  return {
    ...(rest as unknown as PurchaseLot),
    sortingBins: (sortingBins ?? []).map(fromApiBin),
  };
}

export interface SyncOptions {
  onError?: (msg: string) => void;
}

export async function callApi(
  method: string,
  path: string,
  body?: unknown,
): Promise<unknown> {
  const res = await apiFetch(path, {
    method,
    headers:
      body !== undefined ? { "Content-Type": "application/json" } : undefined,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    throw new Error(
      `${method} ${path} → ${res.status}${text ? ` — ${text.slice(0, 200)}` : ""}`,
    );
  }
  if (res.status === 204) return null;
  return res.json();
}

export function fireAndLog(
  p: Promise<unknown>,
  label: string,
  opts: SyncOptions = {},
) {
  p.catch((err) => {
    const msg = err instanceof Error ? err.message : String(err);
    console.error(`[inventorySync] ${label} failed:`, msg);
    opts.onError?.(
      `Sync failed (${label}). Refresh to re-load from the server.`,
    );
  });
}

// ─── Lots ────────────────────────────────────────────────────────────────
export async function fetchLots(): Promise<PurchaseLot[]> {
  const raw = (await callApi("GET", "/api/lots")) as ApiLotResponse[];
  return raw.map(fromApiLot);
}

/** TNK-155 phase E — per-lot singles aggregate (count + cost allocated).
 *  Replaces the `cards.filter(c => c.sourceLotId === id)` scan that
 *  inventory.create / inventory.bulk used to render the lot cost-breakdown
 *  block. The scan broke once phase D made cards lazy-loaded; this endpoint
 *  is the scoped alternative. */
export interface LotCardStats {
  singlesCount: number;
  costAllocated: number;
}
export async function fetchLotCardStats(lotId: string): Promise<LotCardStats> {
  return (await callApi(
    "GET",
    `/api/lots/${encodeURIComponent(lotId)}/card-stats`,
  )) as LotCardStats;
}
export function syncCreateLot(lot: PurchaseLot, opts?: SyncOptions): void {
  const payload = {
    ...lot,
    sortingBins: lot.sortingBins.map((b) => toApiBin(b, lot.id)),
  };
  fireAndLog(callApi("POST", "/api/lots", payload), "create lot", opts);
}
export function syncUpdateLot(lot: PurchaseLot, opts?: SyncOptions): void {
  // PATCH ignores sortingBins on the body — bins are managed via sub-routes.
  // For now we don't sync bin edits independently: when the user mutates a
  // lot's sortingBins through updateLot, we replace the whole bin set.
  const { sortingBins, ...rest } = lot;
  fireAndLog(
    callApi("PATCH", `/api/lots/${encodeURIComponent(lot.id)}`, rest),
    "update lot",
    opts,
  );
  // Reconcile bins: simplest path is delete-all + recreate. For typical
  // lot intake (≤10 bins) this is fine and avoids a diff algorithm.
  fireAndLog(
    (async () => {
      const existing = (await callApi(
        "GET",
        `/api/lots/${encodeURIComponent(lot.id)}`,
      )) as { sortingBins: ApiSortingBin[] };
      const existingIds = new Set(existing.sortingBins.map((b) => b.id));
      const desired = sortingBins.map((b) => toApiBin(b, lot.id));
      const desiredIds = new Set(desired.map((b) => b.id));
      // Delete bins gone from the lot
      for (const b of existing.sortingBins) {
        if (!desiredIds.has(b.id)) {
          await callApi(
            "DELETE",
            `/api/lots/${encodeURIComponent(lot.id)}/sorting-bins/${encodeURIComponent(b.id)}`,
          );
        }
      }
      // Create or patch bins present on the lot
      for (const b of desired) {
        if (existingIds.has(b.id)) {
          await callApi(
            "PATCH",
            `/api/lots/${encodeURIComponent(lot.id)}/sorting-bins/${encodeURIComponent(b.id)}`,
            b,
          );
        } else {
          await callApi(
            "POST",
            `/api/lots/${encodeURIComponent(lot.id)}/sorting-bins`,
            b,
          );
        }
      }
    })(),
    "reconcile sorting bins",
    opts,
  );
}
export function syncDeleteLot(lotId: string, opts?: SyncOptions): void {
  // Cascade-deletes sorting bins server-side; cards keep their stale
  // sourceLotId (matches the in-memory reducer's behavior, which only
  // clears the FK on the cards it knows about — they'll be cleared on
  // next bulk patch).
  fireAndLog(
    callApi("DELETE", `/api/lots/${encodeURIComponent(lotId)}`),
    "delete lot",
    opts,
  );
}

// ─── Cards ───────────────────────────────────────────────────────────────
// TNK-109: pull active + archived in one shot so the inventory tabs can
// switch between them without re-fetching. Server defaults active-only;
// passing ?include=archived returns the full set.
//
// TNK-155 phase F note: this unpaginated full-set fetch is no longer on
// the sign-in critical path (phase D moved it behind `loadCards()`). It
// remains the lazy fallback for tier-2 surfaces that genuinely iterate
// the entire inventory — bundle template availability matching, the
// cost-basis backfill scan, the recommendations + lot-analyzer tools.
// Tier-1 surfaces (/inventory, /inventory/$id, /products, /live) read
// scoped endpoints (/cards/feed, /cards/:id, /cards/by-ids) instead.
export async function fetchCards(): Promise<Card[]> {
  return callApi("GET", "/api/cards?include=archived") as Promise<Card[]>;
}

/** TNK-126 phase 1 — operator-side cards feed sort axes. Mirrors the
 *  storefront's set so the inventory grid can sort the same five ways
 *  buyers can. */
export type CardsFeedSort =
  | "newest"
  | "price-asc"
  | "price-desc"
  | "name-asc"
  | "rarity";

export interface CardsFeedFilters {
  games: string[];
  rarities: string[];
  types: string[];
  elements: string[];
  colors: string[];
  conditions: string[];
  /** Operator-only axis — `["Unlisted", "Listed", "In Package", "Sold"]`. */
  listingStatuses: string[];
  priceMin: number;
  priceMax: number;
  foilOnly: boolean;
  // Operator-only axes (TNK-155 phase A):
  /** setName multi-select. Free-text, IN clause server-side. */
  sets: string[];
  /** Bin name; `"__no_bin__"` selects rows with no/empty storage_location.
   *  Single-select to match the existing UI (one bin or none). */
  bin: string | null;
  /** sourceLotId. Single-select, matches the existing UI. */
  lot: string | null;
  /** `"graded" | "ungraded" | null`. */
  graded: "graded" | "ungraded" | null;
  /** Photo-presence axis — `"with"` = imageFrontRef present,
   *  `"without"` = needs photos. */
  image: "with" | "without" | null;
}

export interface CardsFeedParams {
  limit?: number;
  cursor?: string | null;
  q?: string | null;
  sort?: CardsFeedSort;
  filters?: CardsFeedFilters;
  /** `archived` returns active+archived; `only-archived` returns just
   *  soft-deleted rows. Omit for the default active-only view. */
  include?: "archived" | "only-archived";
}

export interface CardsFeedPage {
  cards: Card[];
  nextCursor: string | null;
  total: number;
}

/**
 * TNK-126 phase 1 — paginated operator-side cards feed. Mirrors the
 * shape of `fetchStorefrontFeed` so consumers can swap once the
 * grid migrates. Server endpoint: `GET /api/cards/feed`.
 */
export async function fetchCardsFeed(
  params: CardsFeedParams = {},
): Promise<CardsFeedPage> {
  const search = new URLSearchParams();
  search.set("limit", String(params.limit ?? 60));
  if (params.cursor) search.set("cursor", params.cursor);
  if (params.q && params.q.trim()) search.set("q", params.q.trim());
  if (params.sort && params.sort !== "newest") search.set("sort", params.sort);
  if (params.include) search.set("include", params.include);
  const f = params.filters;
  if (f) {
    if (f.games.length) search.set("game", f.games.join(","));
    if (f.rarities.length) search.set("rarity", f.rarities.join(","));
    if (f.types.length) search.set("type", f.types.join(","));
    if (f.elements.length) search.set("element", f.elements.join(","));
    if (f.colors.length) search.set("color", f.colors.join(","));
    if (f.conditions.length) search.set("condition", f.conditions.join(","));
    if (f.listingStatuses.length)
      search.set("listingStatus", f.listingStatuses.join(","));
    if (f.priceMin > 0) search.set("priceMin", String(f.priceMin));
    if (f.priceMax > 0) search.set("priceMax", String(f.priceMax));
    if (f.foilOnly) search.set("foil", "1");
    // Operator-only axes (TNK-155 phase A)
    if (f.sets.length) search.set("set", f.sets.join(","));
    if (f.bin) search.set("bin", f.bin);
    if (f.lot) search.set("lot", f.lot);
    if (f.graded) search.set("graded", f.graded);
    if (f.image) search.set("image", f.image);
  }
  return (await callApi(
    "GET",
    `/api/cards/feed?${search.toString()}`,
  )) as CardsFeedPage;
}

/** TNK-155 phase A — operator-side filter facets for /inventory.
 *  Server endpoint: `GET /api/cards/facets`. Returns the picker options
 *  for the axes derived from the card data itself (games / rarities /
 *  sets / supertypes / elements). Bins + lots come from AppContext
 *  (`storageBins` + `lots`); conditions are a static enum. */
export interface CardsFacets {
  games: string[];
  rarities: string[];
  sets: string[];
  supertypes: string[];
  elements: string[];
  hasGraded: boolean;
}

export async function fetchCardsFacets(opts?: {
  include?: "archived" | "only-archived";
}): Promise<CardsFacets> {
  const search = new URLSearchParams();
  if (opts?.include) search.set("include", opts.include);
  const qs = search.toString();
  return (await callApi(
    "GET",
    `/api/cards/facets${qs ? `?${qs}` : ""}`,
  )) as CardsFacets;
}

/** TNK-155 phase C — scoped "cards by id" fetcher. Backs
 *  `useCardsByIds` for product-detail / pick / live screens that
 *  previously filtered the entire AppContext.cards array. Server
 *  caps the request at 500 ids; larger jobs should paginate via
 *  `/cards/feed` with a scoped filter axis. */
export async function fetchCardsByIds(ids: string[]): Promise<Card[]> {
  if (ids.length === 0) return [];
  return (await callApi("POST", "/api/cards/by-ids", { ids })) as Card[];
}

/** TNK-155 phase B — single-card lookup. Backs `useInventoryCardLookup`
 *  for the inventory detail page when the card isn't in the warmed
 *  AppContext.cards (phase D removes the eager hydration; this becomes
 *  the primary path). Returns null on 404 so the hook can render a
 *  "card not found" empty state. Uses `apiFetch` directly (not callApi)
 *  to inspect the status code without parsing a stringified error. */
export async function fetchInventoryCard(id: string): Promise<Card | null> {
  const res = await apiFetch(`/api/cards/${encodeURIComponent(id)}`);
  if (res.status === 404) return null;
  if (!res.ok) {
    throw new Error(`fetchInventoryCard ${id} → ${res.status}`);
  }
  return (await res.json()) as Card;
}

/** Soft-delete a card (TNK-109). Awaited so the caller can surface a 409
 *  when the card is packaged into a product — server returns
 *  `{ error, packagedProductId }`. */
export async function archiveCardRequest(id: string): Promise<Card> {
  return (await callApi(
    "POST",
    `/api/cards/${encodeURIComponent(id)}/archive`,
    {},
  )) as Card;
}

/** Clear archivedAt on a soft-deleted card. */
export async function restoreCardRequest(id: string): Promise<Card> {
  return (await callApi(
    "POST",
    `/api/cards/${encodeURIComponent(id)}/restore`,
    {},
  )) as Card;
}
export function syncCreateCard(card: Card, opts?: SyncOptions): void {
  fireAndLog(callApi("POST", "/api/cards", card), "create card", opts);
}
export function syncCreateCards(
  cards: Card[],
  opts?: SyncOptions,
): Promise<void> {
  if (cards.length === 0) return Promise.resolve();
  const p = callApi("POST", "/api/cards/bulk", cards);
  fireAndLog(p, "bulk create cards", opts);
  // Resolve (never reject) so callers can `await` to sequence follow-up work
  // (e.g. set-resolution PATCHes) after the create lands without their own
  // try/catch — fireAndLog already surfaces the error to the sync banner.
  return p.then(
    () => undefined,
    () => undefined,
  );
}
export function syncUpdateCard(card: Card, opts?: SyncOptions): void {
  fireAndLog(
    callApi("PATCH", `/api/cards/${encodeURIComponent(card.id)}`, card),
    "update card",
    opts,
  );
}
export function syncDeleteCard(cardId: string, opts?: SyncOptions): void {
  fireAndLog(
    callApi("DELETE", `/api/cards/${encodeURIComponent(cardId)}`),
    "delete card",
    opts,
  );
}
export function syncBulkPatchCards(
  patches: CardPatch[],
  opts?: SyncOptions,
): void {
  if (patches.length === 0) return;
  fireAndLog(
    callApi("PATCH", "/api/cards/bulk", patches),
    "bulk patch cards",
    opts,
  );
}
export function syncBulkDeleteCards(
  cardIds: string[],
  opts?: SyncOptions,
): void {
  if (cardIds.length === 0) return;
  fireAndLog(
    callApi("DELETE", "/api/cards/bulk", { ids: cardIds }),
    "bulk delete cards",
    opts,
  );
}

// Storage bins are containers now (TNK-191) — see fetchContainers + the
// container sync helpers below. The old /api/storage-bins client helpers were
// removed when bins collapsed into the tree.

// ─── Locations (TNK-103 Phase G) ─────────────────────────────────────────────
/** A physical site that contains StorageBins — "Home", "Shop — Main St". */
export interface ApiLocation {
  id: string;
  name: string;
  kind: "storage" | "retail";
  archivedAt?: string;
  createdAt: string;
}
/** Default GET excludes archived; widen via include='archived' for the UI's
 *  archived tab when one ships. */
export function fetchLocations(): Promise<ApiLocation[]> {
  return callApi("GET", "/api/locations?include=archived") as Promise<
    ApiLocation[]
  >;
}
export function syncCreateLocation(loc: ApiLocation, opts?: SyncOptions): void {
  fireAndLog(callApi("POST", "/api/locations", loc), "create location", opts);
}
export function syncUpdateLocation(loc: ApiLocation, opts?: SyncOptions): void {
  fireAndLog(
    callApi("PATCH", `/api/locations/${encodeURIComponent(loc.id)}`, loc),
    "update location",
    opts,
  );
}
export async function archiveLocationRequest(id: string): Promise<ApiLocation> {
  return (await callApi(
    "POST",
    `/api/locations/${encodeURIComponent(id)}/archive`,
    {},
  )) as ApiLocation;
}
export async function restoreLocationRequest(id: string): Promise<ApiLocation> {
  return (await callApi(
    "POST",
    `/api/locations/${encodeURIComponent(id)}/restore`,
    {},
  )) as ApiLocation;
}

// ─── Containers (TNK-187 / epic TNK-186) ─────────────────────────────────────
/** The arbitrary-depth storage tree. GET returns active + archived; the UI
 *  filters. Runs alongside bins/locations during the additive Phase A. */
export function fetchContainers(): Promise<Container[]> {
  return callApi("GET", "/api/containers?include=archived") as Promise<
    Container[]
  >;
}
export function syncCreateContainer(c: Container, opts?: SyncOptions): void {
  fireAndLog(callApi("POST", "/api/containers", c), "create container", opts);
}
export function syncUpdateContainer(c: Container, opts?: SyncOptions): void {
  fireAndLog(
    callApi("PATCH", `/api/containers/${encodeURIComponent(c.id)}`, c),
    "update container",
    opts,
  );
}
export async function archiveContainerRequest(id: string): Promise<Container> {
  return (await callApi(
    "POST",
    `/api/containers/${encodeURIComponent(id)}/archive`,
    {},
  )) as Container;
}
export async function restoreContainerRequest(id: string): Promise<Container> {
  return (await callApi(
    "POST",
    `/api/containers/${encodeURIComponent(id)}/restore`,
    {},
  )) as Container;
}
/** Awaited (not fire-and-forget) — the server 409s if the container still has
 *  children or placements, and the caller surfaces that. */
export async function deleteContainerRequest(id: string): Promise<void> {
  await callApi("DELETE", `/api/containers/${encodeURIComponent(id)}`);
}

export interface CardPlacement {
  containerId: string;
  quantity: number;
}
/** TNK-189 lookup — every placement of a card across containers (incl. archived
 *  containers; the client marks them). Path is built client-side from the
 *  cached container tree. */
export function fetchCardPlacements(cardId: string): Promise<CardPlacement[]> {
  return callApi(
    "GET",
    `/api/placements/by-card/${encodeURIComponent(cardId)}`,
  ) as Promise<CardPlacement[]>;
}

// ─── Chaos intake (TNK-188 / epic TNK-186) ──────────────────────────────────
export interface IntakeBin {
  id: string;
  name: string;
  capacityMax?: number;
  total: number;
}
export interface IntakeSession {
  containerId: string | null;
  bin: IntakeBin | null;
}
export interface IntakeScanResult {
  matched: boolean;
  created?: boolean;
  card?: Card;
  eventId?: string;
  binTotal?: number;
  capacityMax?: number | null;
}
export interface IntakeUndoResult {
  undone: boolean;
  card?: Card | null;
  cardArchived?: boolean;
  containerId?: string;
  binTotal?: number;
}

export function fetchIntakeSession(): Promise<IntakeSession> {
  return callApi("GET", "/api/intake/session") as Promise<IntakeSession>;
}
export async function setIntakeSession(
  containerId: string,
): Promise<IntakeSession> {
  return (await callApi("PUT", "/api/intake/session", {
    containerId,
  })) as IntakeSession;
}
/** Awaited — the scan hot-path needs the resolved card + running bin total to
 *  render the session list and the capacity warning. */
export async function intakeScanRequest(payload: {
  game: string;
  setCode: string;
  number: string;
  condition: string;
}): Promise<IntakeScanResult> {
  return (await callApi(
    "POST",
    "/api/intake/scan",
    payload,
  )) as IntakeScanResult;
}
export async function intakeUndoRequest(): Promise<IntakeUndoResult> {
  return (await callApi("POST", "/api/intake/undo", {})) as IntakeUndoResult;
}

// ─── Migration helper ────────────────────────────────────────────────────
/**
 * One-shot uploader: POSTs every lot, card, and storage bin currently in
 * the in-memory snapshot to the server. Used by the migration banner
 * after a sign-in when the server returned empty but localStorage has
 * data.
 *
 * Awaits all POSTs so callers can clear localStorage on success.
 */
export async function uploadAllInventory(snapshot: {
  lots: PurchaseLot[];
  cards: Card[];
  storageBins: StorageBin[];
}): Promise<void> {
  for (const lot of snapshot.lots) {
    await callApi("POST", "/api/lots", {
      ...lot,
      sortingBins: lot.sortingBins.map((b) => toApiBin(b, lot.id)),
    });
  }
  // Bins are containers now (TNK-191); the legacy localStorage→server bin
  // upload is obsolete and `snapshot.storageBins` is always empty.
  if (snapshot.cards.length > 0) {
    // Chunk bulk-create at 500 cards/request to stay comfortably under the
    // 10MB body cap even with large cards.
    const CHUNK = 500;
    for (let i = 0; i < snapshot.cards.length; i += CHUNK) {
      await callApi(
        "POST",
        "/api/cards/bulk",
        snapshot.cards.slice(i, i + CHUNK),
      );
    }
  }
}
